import {isHTMLInputElement} from "../helper/Validation";

const HTMLLoaderTemplate = `
<div class="d-none position-absolute top-0 start-0 w-100 h-100 bg-dark-subtle" style="z-index: 10;">
    <div class="d-flex justify-content-center align-items-center h-100">
        <div class="text-center"><i class="far fa-fw fa-loader fa-spin text-primary"></i></div>
    </div>
</div>
`;

const CropperMixin = (() => {
    function createDOMElements(context, cropArea) {
        let realImage = null;
        if (cropArea.dataset.src && cropArea.dataset.src !== '') {
            realImage = createElement('img', {
                id: 'CropperJSRealImage',
                src: cropArea.dataset.src?.trim() || '',
                alt: cropArea.dataset.alt?.trim() || '',
                className: 'w-100 img-fluid mx-auto'
            });
            realImage.classList.add(...context.imageClasses);
        }

        const previewImage = createElement('img', {
            id: 'CropperJSPreviewImage',
            className: 'd-none w-100 img-fluid'
        });

        const loader = document.createElement('div');
        loader.innerHTML = HTMLLoaderTemplate;

        const imageContainer = createElement('div', {
            className: 'position-relative d-flex flex-column align-items-center'
        });

        imageContainer.appendChild(previewImage);
        if (realImage) imageContainer.appendChild(realImage);

        cropArea.appendChild(loader);
        cropArea.appendChild(imageContainer);

        return {realImage, previewImage, loader};
    }

    function createElement(tagName, attributes = {}) {
        const element = document.createElement(tagName);

        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'className') {
                element.className = value.toString();
            } else {
                element.setAttribute(key, value.toString());
            }
        });

        return element;
    }

    function loadImageFile(context, file, previewImage, loader, realImage, inputField) {
        previewImage.classList.remove('d-none');

        const reader = new FileReader();

        reader.onload = (event) => {
            previewImage.src = event.target.result;

            previewImage.onload = () => {
                context.cropper = new Cropper(previewImage, {
                    ...context.options,
                    ready: () => loader?.classList.add('d-none'),
                    error: (error) => {
                        console.error('Fehler beim Initialisieren des Croppers:', error);
                        handleCropperError(context, previewImage, loader, realImage, inputField);
                    }
                });
            };

            previewImage.onerror = () => {
                console.error('Fehler beim Laden des Bildes');
                handleCropperError(context, previewImage, loader, realImage, inputField);
            };
        };

        reader.onerror = () => {
            console.error('Fehler beim Lesen der Datei');
            handleCropperError(context, previewImage, loader, realImage, inputField);
        };

        reader.readAsDataURL(file);
    }

    function handleCropperError(context, previewImage, loader, realImage, inputField) {
        loader?.classList.add('d-none');
        context.clear();
        inputField.value = '';
        previewImage.classList.add('d-none');
        realImage?.classList.remove('d-none');
    }

    return {
        cropper: null,
        options: {
            aspectRatio: 16 / 9,
            viewMode: 1,
            dragMode: 'crop',
            background: false,
            autoCropArea: 0.6,
            restore: false,
            guides: true,
            center: true,
            highlight: true,
            cropBoxMovable: true,
            cropBoxResizable: true,
            toggleDragModeOnDblclick: false,
            minContainerWidth: 400,
            minContainerHeight: 300,
            scalable: false,
            zoomable: false,
            zoomOnTouch: false,
            zoomOnWheel: false,
            checkOrientation: false,
            responsive: true,
            modal: true
        },
        imageClasses: [],
        originalImageSrc: null,
        hasFileSelected: false,
        initCropper(cropArea, inputField) {
            if (!cropArea || !inputField) {
                console.error('Es wurde kein Bereich oder kein Input-Feld definiert');
                return;
            }

            if (!isHTMLInputElement(inputField)) {
                console.error('Das angegebene Input-Feld ist kein HTMLInputElement');
                return;
            }

            this.clear();

            // Erstelle und konfiguriere DOM-Elemente
            const elements = createDOMElements(this, cropArea);
            const {realImage, previewImage, loader} = elements;

            // Ursprüngliche Bild-URL speichern
            this.originalImageSrc = realImage?.src || null;
            this.hasFileSelected = false;

            inputField.addEventListener('change', (e) => {
                const file = e.target.files[0];
                if (!file) {
                    this.hasFileSelected = false;
                    return;
                }

                realImage?.classList.add('d-none');
                loader?.classList.remove('d-none');

                // Markiere, dass eine neue Datei ausgewählt wurde
                this.hasFileSelected = true;

                // Cleanup vorheriger Cropper-Instanz
                this.destroyCropper();

                loadImageFile(this, file, previewImage, loader, realImage, inputField);
            });

            // Cleanup beim Verlassen der Seite
            window.addEventListener('beforeunload', () => this.clear());
        },
        destroyCropper() {
            if (this.cropper) {
                try {
                    // Verschiedene Destroy-Methoden testen
                    if (typeof this.cropper.destroy === 'function') {
                        this.cropper.destroy();
                    } else if (typeof this.cropper.clear === 'function') {
                        this.cropper.clear();
                    } else if (typeof this.cropper.reset === 'function') {
                        this.cropper.reset();
                    }
                } catch (error) {
                    console.error('Fehler beim Zerstören des Croppers:', error);
                } finally {
                    this.cropper = null; // Immer auf null setzen
                }
            }
        },
        clear() {
            this.destroyCropper();
            removeEventListener('beforeunload', this.window);
            this.hasFileSelected = false;
            this.originalImageSrc = null;
        },
        getCropData() {
            if (!this.cropper) {
                console.error('Cropper ist nicht initialisiert');
                return null;
            }

            const cropData = this.cropper.getData();

            return {
                x: Math.round(cropData.x),
                y: Math.round(cropData.y),
                width: Math.round(cropData.width),
                height: Math.round(cropData.height)
            };
        },
        hasChanges() {
            // Prüfe, ob eine neue Datei ausgewählt wurde
            if (this.hasFileSelected) {
                return true;
            }

            // Prüfe, ob ein Cropper aktiv ist und gültige Crop-Daten hat
            if (this.cropper) {
                try {
                    const cropData = this.cropper.getData();
                    // Prüfe ob die Crop-Daten gültig sind (nicht leer oder ungültig)
                    return cropData &&
                        typeof cropData.x === 'number' &&
                        typeof cropData.y === 'number' &&
                        typeof cropData.width === 'number' &&
                        typeof cropData.height === 'number' &&
                        cropData.width > 0 &&
                        cropData.height > 0;
                } catch (error) {
                    console.error('Fehler beim Abrufen der Crop-Daten:', error);
                    return false;
                }
            }

            return false;
        },
        setOptions(options) {
            this.options = {...this.options, ...options};
        },
        setImageClasses(classes) {
            if (!Array.isArray(classes)) {
                this.imageClasses = [classes];
            }
            this.imageClasses = classes;
        }
    };
})();

export default CropperMixin;
